"use client";

/**
 * Page-state grounding for Amanda (Phase 1 of the Amanda AI Grounding Plan).
 *
 * Pages that want Amanda to ground answers in what the user is actually
 * seeing publish a structured payload via `useChatPageState(route, payload)`.
 * The chat widget reads the latest payload via `useChatPageStateSnapshot()`
 * and includes it in every chat-message request to the backend.
 *
 * Without this, Amanda has no live signal about UI state — she has to
 * reason from a static knowledge base, which is the root cause of the
 * UI-hallucination class of bugs we hit in production (see
 * AMANDA_AI_GROUNDING_PLAN.md, Britany Creekmore's conversation
 * fd04552b).
 *
 * Schema is intentionally untyped (`Record<string, unknown>`) so each
 * page can publish what's relevant without coordinating a union type.
 * The backend treats it as JSON and renders it into the system prompt.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import type { PageState } from "@/lib/pageState";

export type ChatPageStatePayload = Record<string, unknown> & {
  /** Legacy logical route name ("envelope_editor") OR a flat pattern; the backend maps both. */
  route: string;
};

/** Generic part published by the workspace layout (spec §5.4). */
export type GenericPageState = Omit<PageState, "extra" | "resource" | "selection">;

interface ChatPageStateContextValue {
  /** Snapshot the chat widget reads at request time: generic ∪ extra. */
  getSnapshot: () => PageState | null;
  /** Layout: publish the generic payload (route, params, org, modal, errors, ui). */
  publishGeneric: (generic: GenericPageState | null) => void;
  /** Pages: publish their rich payload; becomes `extra` (+ resource/selection when given). */
  publishExtra: (payload: ChatPageStatePayload | null) => void;
  /** Identity of the last extra payload, so an unmount only clears its own. */
  lastExtra: () => ChatPageStatePayload | null;
}

const ChatPageStateContext = createContext<ChatPageStateContextValue | null>(null);

function merge(generic: GenericPageState | null, extra: ChatPageStatePayload | null): PageState | null {
  if (!generic && !extra) return null;
  const base: PageState = generic
    ? { ...generic }
    : { route: String(extra?.route ?? "/"), params: {}, org: { slug: null, role: null }, errors: [], ui: [] };
  if (extra) {
    const { route: _r, resource, selection, ...rest } = extra as Record<string, unknown>;
    if (resource && typeof resource === "object") base.resource = resource as PageState["resource"];
    if (selection && typeof selection === "object") base.selection = selection as Record<string, unknown>;
    // A page's legacy logical route rides along so the backend can map it.
    base.extra = { ...rest, ...(typeof _r === "string" && !_r.startsWith("/") ? { legacy_route: _r } : {}) };
    if (!generic && typeof _r === "string") base.route = _r;
  }
  return base;
}

export function ChatPageStateProvider({ children }: { children: ReactNode }) {
  // Refs, not state: the snapshot is read at REQUEST time by the chat engine, so
  // a publish must not re-render every consumer (the editors publish per
  // keystroke and the layout on every DOM mutation). The context value is one
  // stable object for the provider's lifetime.
  const genericRef = useRef<GenericPageState | null>(null);
  const extraRef = useRef<ChatPageStatePayload | null>(null);

  const publishGeneric = useCallback((generic: GenericPageState | null) => {
    genericRef.current = generic;
  }, []);
  const publishExtra = useCallback((payload: ChatPageStatePayload | null) => {
    extraRef.current = payload;
  }, []);
  const getSnapshot = useCallback(() => merge(genericRef.current, extraRef.current), []);
  const lastExtra = useCallback(() => extraRef.current, []);
  const value = useMemo(() => ({ getSnapshot, publishGeneric, publishExtra, lastExtra }),
                        [getSnapshot, publishGeneric, publishExtra, lastExtra]);

  return <ChatPageStateContext.Provider value={value}>{children}</ChatPageStateContext.Provider>;
}

/** Layout-side hook: returns the generic publisher (or null outside the provider). */
export function useChatPageStatePublisher() {
  const ctx = useContext(ChatPageStateContext);
  return ctx ? ctx.publishGeneric : null;
}

/**
 * Page-side hook. Pass the page's rich state; it republishes on every change
 * and clears its own payload on unmount so a stale page never leaks.
 */
export function useChatPageState(payload: ChatPageStatePayload | null) {
  const ctx = useContext(ChatPageStateContext);
  const serialized = payload ? JSON.stringify(payload) : null;

  useEffect(() => {
    if (!ctx) return;
    ctx.publishExtra(payload);
    return () => {
      if (ctx.lastExtra() === payload) {
        ctx.publishExtra(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);
}

/**
 * Chat-widget-side hook. Use only inside the chat client to read the
 * latest snapshot when building a request body.
 */
export function useChatPageStateSnapshot() {
  const ctx = useContext(ChatPageStateContext);
  return ctx ? ctx.getSnapshot : () => null;
}
